import cloneDeep from 'lodash/cloneDeep'
import {
  MutationUpdaterFunction,
  DefaultContext,
  ApolloCache,
} from '@apollo/client'

import { GetBucketListItemsIndexDocument } from '../../../hooks/useQueries'

export type BucketListHandlerInput = {
  itemId: string
  itemType: GQL_Searchable
  bucketListId: string
}

export const makeItemKey = ({
  itemId,
  itemType,
}: Pick<BucketListHandlerInput, 'itemId' | 'itemType'>): string => {
  switch (itemType) {
    case 'Adventure':
    case 'Listing':
    case 'Story':
      return `${itemType}:${itemId}`
    default:
      throw new Error(`Unexpected itemType ${itemType} received`)
  }
}

export const mapGQLTypenameToGQLSearchable = (
  typename:
    | GQL_StoryAdventure['__typename']
    | GQL_ListingAdventure['__typename']
    | GQL_Story['__typename']
    | GQL_Listing['__typename'],
): GQL_Searchable => {
  switch (typename) {
    case 'StoryAdventure':
    case 'ListingAdventure':
      return 'Adventure'
    case 'Story':
      return 'Story'
    case 'Listing':
      return 'Listing'
    default:
      throw new Error(`Unhandled search result with __typename: ${typename}`)
  }
}

type MutationReturnBucketListItem =
  'Listing' | 'Story' | 'StoryAdventure' | 'ListingAdventure'

type BuildUpdateFnArgs = {
  itemId: string
  itemType: GQL_Searchable
  bucketListId: string
}

const buildAddToBucketListMutationUpdateFn =
  ({
    itemId,
    itemType,
    bucketListId,
  }: BuildUpdateFnArgs): MutationUpdaterFunction<
    GQL_AddToBucketListMutation,
    Exact<{
      bucketListId: string
      itemId: string
      itemType: GQL_Searchable
      source: string
    }>,
    DefaultContext,
    ApolloCache<any>
  > =>
  (cache) => {
    const prevData = cache.readQuery<GQL_GetBucketListItemsIndexQuery>({
      query: GetBucketListItemsIndexDocument,
    })

    const nextData = cloneDeep(prevData)

    const itemKey = makeItemKey({ itemId, itemType })

    cache.writeQuery({
      query: GetBucketListItemsIndexDocument,
      data: {
        ...nextData,
        currentUser: {
          ...(nextData?.currentUser || {}),
          bucketListItemsIndex: {
            ...(nextData?.currentUser?.bucketListItemsIndex || {}),
            [itemKey]: [
              ...(nextData?.currentUser?.bucketListItemsIndex?.[itemKey] || []),
              bucketListId,
            ],
          },
        },
      },
    })
  }

const buildRemoveFromBucketListMutationUpdateFn =
  ({
    itemId,
    itemType,
    bucketListId,
  }: BuildUpdateFnArgs): MutationUpdaterFunction<
    GQL_RemoveFromBucketListMutation,
    Exact<{
      bucketListId: string
      itemId: string
      itemType: GQL_Searchable
      source: string
    }>,
    DefaultContext,
    ApolloCache<any>
  > =>
  (cache) => {
    const prevData = cache.readQuery<GQL_GetBucketListItemsIndexQuery>({
      query: GetBucketListItemsIndexDocument,
    })

    const nextData = cloneDeep(prevData)

    const itemKey = makeItemKey({ itemId, itemType })

    cache.writeQuery({
      query: GetBucketListItemsIndexDocument,
      data: {
        ...nextData,
        currentUser: {
          ...(nextData?.currentUser || {}),
          bucketListItemsIndex: {
            ...(nextData?.currentUser?.bucketListItemsIndex || {}),
            [itemKey]: [
              ...(nextData?.currentUser?.bucketListItemsIndex?.[
                itemKey
              ]?.filter((id: string) => id !== bucketListId) || []),
            ],
          },
        },
      },
    })
  }

const buildAddToBucketListOptimisticResponse = ({
  itemId,
  itemType,
}: BuildUpdateFnArgs) =>
  ({
    __typename: 'Mutation',
    addToBucketList: {
      __typename: 'AddToBucketListPayload',
      bucketListItem: {
        __typename: 'BucketListItem',
        item: {
          __typename: itemType as MutationReturnBucketListItem,
          id: itemId,
        },
      },
    },
  }) as const

const buildRemoveFromBucketListOptimisticResponse = ({
  itemId,
  itemType,
}: BuildUpdateFnArgs) =>
  ({
    __typename: 'Mutation',
    removeFromBucketList: {
      __typename: 'RemoveFromBucketListPayload',
      bucketListItem: {
        __typename: 'BucketListItem',
        item: {
          __typename: itemType as MutationReturnBucketListItem,
          id: itemId,
        },
      },
    },
  }) as const

/*
  For mutations to add/remove items to/from bucket lists, specifying both an optimisticResponse and update
  function ensures that:

  1) The bucket list items index in the apollo cache is updated correctly following the mutation, so UI can update
  2) This happens immediately on user input (i.e. before server responds)

  As per apollo docs, the update function in this case is called twice: once for the optimistic response, and once
  for the actual response from the server.
*/

export const buildMutationOptionsAddToBucketList = ({
  itemId,
  itemType,
  bucketListId,
}: BuildUpdateFnArgs) => ({
  update: buildAddToBucketListMutationUpdateFn({
    itemId,
    itemType,
    bucketListId,
  }),
  optimisticResponse: buildAddToBucketListOptimisticResponse({
    itemId,
    itemType,
    bucketListId,
  }),
})

export const buildMutationOptionsRemoveFromBucketList = ({
  itemId,
  itemType,
  bucketListId,
}: BuildUpdateFnArgs) => ({
  update: buildRemoveFromBucketListMutationUpdateFn({
    itemId,
    itemType,
    bucketListId,
  }),
  optimisticResponse: buildRemoveFromBucketListOptimisticResponse({
    itemId,
    itemType,
    bucketListId,
  }),
})
